import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { LoaderModal, SubmitButton } from 'components';
import { Button, Message, Text } from 'components/chroma';

import {
  SegmentedControlOptions,
  SegmentedControlQuestion,
} from 'scenes/common/components/Questions';
import { toCurrencyWithSign } from 'scenes/common/helpers/currency';
import estimatedSumInsuredApi from 'scenes/common/api/quote/estimatedSumInsured';
import {
  ApiData,
  BuildingType,
  SUM_INSURED_VALUES,
  InsuranceName,
  SingleFieldStepProps,
  SumInsuredType,
} from 'scenes/common/types';
import {
  getApiData,
  isApiError,
  makeApiSuccess,
  NOT_YET_LOADED,
} from 'scenes/common/helpers/apiData';
import {
  EstimatedSumInsured,
  EstimatedSumInsuredProfile,
} from 'scenes/common/api/quote/estimatedSumInsured/types';
import { sumInsuredEstimatingCalculatorUrl } from 'scenes/common/helpers/contacts';

import styled from 'styled-components';
import HelpTextModal from './HelpTextModal/HelpTextModal';

const STATE_KEY = 'sumInsuredType';

export const SUM_INSURED_TYPE_QUESTION =
  "What is the total cost to rebuild the home at today's prices?";
export const HELP_LINK_CAPTION = 'What is considered as rebuilding costs?';
export const ERROR_MESSAGE_TEXT =
  'An unexpected error occurred while retrieving estimated building sum insured value.';

const LinkWrapper = styled.div`
  margin-bottom: 2rem;
`;

const SumInsuredChoiceStep: FunctionComponent<
  Pick<SingleFieldStepProps<SumInsuredType>, 'onNext' | 'defaultStateValue'> & {
    insuranceName: InsuranceName;
    estimatedSumInsuredProfile: EstimatedSumInsuredProfile;
    buildingType: BuildingType;
  }
> = ({
  onNext,
  insuranceName,
  defaultStateValue,
  estimatedSumInsuredProfile,
  buildingType,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    getValues,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [estimatedSumInsuredData, setEstimatedSumInsuredData] = useState<
    ApiData<EstimatedSumInsured>
  >(NOT_YET_LOADED);

  const isEstimatedSumInsuredNotAvailable =
    !getApiData(estimatedSumInsuredData)?.estimatedValue && !isLoading;
  useEffect(() => {
    const estimatedSumInsured = SUM_INSURED_VALUES[buildingType];
    if (estimatedSumInsured) {
      setEstimatedSumInsuredData(makeApiSuccess(estimatedSumInsured));
    } else {
      setIsLoading(true);
      estimatedSumInsuredApi()
        .estimateSumInsured(insuranceName, estimatedSumInsuredProfile)
        .then((sumInsured) => {
          if (isApiError(sumInsured)) {
            setEstimatedSumInsuredData(
              makeApiSuccess(
                SUM_INSURED_VALUES.notAvailable as EstimatedSumInsured
              )
            );
          } else {
            setEstimatedSumInsuredData(sumInsured);
          }
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(() => {
    const { estimatedValue, minLimit, maxLimit } = getApiData(
      estimatedSumInsuredData
    );
    onNext({
      sumInsuredType: getValues(STATE_KEY),

      estimatedSumInsured: estimatedValue,
      agreedSumInsuredRange: {
        lowerLimit: minLimit.toString(),
        upperLimit: maxLimit.toString(),
      },
    });
  }, [estimatedSumInsuredData, onNext, getValues]);

  const SUM_INSURED_OPTIONS: SegmentedControlOptions<
    string,
    SumInsuredType
  > = (isEstimatedSumInsuredNotAvailable && [
    { label: SumInsuredType.Entered, value: SumInsuredType.Agreed },
  ]) || [
    {
      label: `${toCurrencyWithSign(
        getApiData(estimatedSumInsuredData)?.estimatedValue ?? ''
      )} (estimated)`,
      value: SumInsuredType.Estimated,
    },
    { label: SumInsuredType.Agreed, value: SumInsuredType.Agreed },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SegmentedControlQuestion
        errors={errors}
        control={control}
        helpText={
          <Text>
            This estimated rebuilding cost is a guide only and is made with the
            assumption of your home being of <strong>standard</strong> quality
            and on a <strong>flat/gentle</strong> slope. If this assumption is
            incorrect or you require more accurate estimates you can use a
            detailed estimating{' '}
            <a
              href={sumInsuredEstimatingCalculatorUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              calculator
            </a>{' '}
            or check with a suitable qualified professional.
          </Text>
        }
        options={SUM_INSURED_OPTIONS}
        stateKey={STATE_KEY}
        question={SUM_INSURED_TYPE_QUESTION}
        defaultValue={defaultStateValue}
        validationRules={{
          validate: {
            hasData: () =>
              !isApiError(estimatedSumInsuredData) || ERROR_MESSAGE_TEXT,
          },
        }}
      />

      {isEstimatedSumInsuredNotAvailable && (
        <Text color="primary">
          A sum insured estimate cannot be provided right now. You can still
          nominate a sum insured value of your choice if you wish to proceed
          with a quote. You could try using this third party sum insured{' '}
          <a
            href={sumInsuredEstimatingCalculatorUrl()}
            target="_blank"
            rel="noopener noreferrer"
          >
            calculator
          </a>{' '}
          to create a detailed estimate.
        </Text>
      )}
      <LinkWrapper>
        <Message info link>
          <Button
            link
            id="what-does-this-mean-link"
            ariaLabel={`${HELP_LINK_CAPTION} Opens dialog`}
            onClick={() => setShowModal(true)}
          >
            {HELP_LINK_CAPTION}
          </Button>
        </Message>
      </LinkWrapper>

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
      <HelpTextModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <LoaderModal isOpen={isLoading} />
    </form>
  );
};

export default SumInsuredChoiceStep;
